import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock, Plus } from 'lucide-react';
import { createRole } from '@mkcore/platform/schema';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  PageHeader,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '@mkcore/ui';
import { newIdempotencyKey } from '@/lib/api';
import { useActionMutation } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { rolesQuery } from '@/features/admin/queries';

export const Route = createFileRoute('/_app/admin/roles/')({ component: Roles });

function Roles() {
  const { t } = useTranslation();
  const roles = useQuery(rolesQuery);
  const canCreate = useCan('admin:create');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: '', name: '', description: '' });
  const [key] = useState(newIdempotencyKey);
  const create = useActionMutation(createRole, {
    invalidate: [['admin', 'role']],
    success: t('roles.created'),
    onSuccess: (r) => void navigate({ to: '/admin/roles/$id', params: { id: r.id } }),
  });
  return (
    <>
      <PageHeader
        eyebrow={t('nav.admin')}
        title={t('roles.title')}
        description={t('roles.description')}
        actions={
          canCreate && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> {t('roles.new')}
            </Button>
          )
        }
      />
      {!roles.data ? (
        <Skeleton className="h-64" />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('roles.key')}</Th>
              <Th className="hidden sm:table-cell">{t('roles.permissions')}</Th>
              <Th>{t('roles.members')}</Th>
            </Tr>
          </THead>
          <TBody>
            {roles.data.items.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <Link to="/admin/roles/$id" params={{ id: r.id }} className="font-bold hover:underline">
                    {r.name}
                  </Link>
                  <div className="text-xs text-fg-3">{r.description}</div>
                </Td>
                <Td>
                  <span className="font-mono text-xs">{r.key}</span>{' '}
                  {r.isSystem && (
                    <Badge tone="info">
                      <Lock className="size-3" /> {t('roles.system')}
                    </Badge>
                  )}{' '}
                  {r.tenantWideScope && <Badge tone="outline">{t('roles.tenantWide')}</Badge>}
                </Td>
                <Td className="hidden sm:table-cell tabular">{r.permissionKeys.length}</Td>
                <Td className="tabular">{r.memberCount}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('roles.new')}</DialogTitle>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({ key, input: { ...form } });
            }}
          >
            <Field label={t('common.name')}>{(p) => <Input {...p} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
            <Field label={t('roles.key')} hint="lowercase-with-dashes">
              {(p) => <Input {...p} required pattern="[a-z][a-z0-9-]*" className="font-mono" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />}
            </Field>
            <Field label={t('common.details')}>
              {(p) => <Textarea {...p} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />}
            </Field>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending}>
                {t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
